import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Form, Input, Layout, Row, Select, Space, Typography } from "antd";
import { notifyError, notifySuccess } from "../utils/notification";
import {
    getInceptionCompanies,
    getInceptionCredentialTemplates,
    getInceptionSettings,
    testInceptionConnection,
    updateInceptionSettings
} from "./actions";

const { Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;

const buildCompanyKey = (company: any) => `${company?.buildingId || ""}::${company?.tenantId || ""}`;
const getErrorMessage = (error: any, fallback: string) => {
    return error?.response?.data?.message || error?.message || fallback;
};

export const InceptionSettingsView = () => {
    const [companies, setCompanies] = useState<Array<any>>([]);
    const [templateOptions, setTemplateOptions] = useState<Array<{ label: string, value: string }>>([]);
    const [selectedCompanyKey, setSelectedCompanyKey] = useState<string>("");
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [hasUserToken, setHasUserToken] = useState(false);
    const [form] = Form.useForm();

    const loadCompanies = useCallback(async () => {
        setIsLoadingCompanies(true);
        try {
            const companyList = await getInceptionCompanies();
            setCompanies(companyList);
        } catch (error) {
            console.error("Error loading companies:", error);
            setCompanies([]);
            notifyError("Failed to load companies");
        } finally {
            setIsLoadingCompanies(false);
        }
    }, []);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    useEffect(() => {
        if (!selectedCompanyKey) {
            setHasUserToken(false);
            setTemplateOptions([]);
            form.resetFields();
            return;
        }

        setHasUserToken(false);
        setTemplateOptions([]);
        form.resetFields();
    }, [form, selectedCompanyKey]);

    const selectedCompany = companies.find((company: any) => buildCompanyKey(company) === selectedCompanyKey);
    const selectedBuildingId = selectedCompany?.buildingId;
    const selectedTenantId = selectedCompany?.tenantId;

    const loadSettings = useCallback(async (tenantId: string) => {
        if (!selectedBuildingId) {
            notifyError("Missing company context");
            return;
        }
        setIsLoading(true);
        try {
            const settings = await getInceptionSettings(selectedBuildingId, tenantId);
            setHasUserToken(!!settings?.hasUserToken);
            form.setFieldsValue({
                skytunnelUrl: settings?.skytunnelUrl || "",
                templateId: settings?.templateId || "",
                userToken: ""
            });

            try {
                const templateData = await getInceptionCredentialTemplates(selectedBuildingId, tenantId);
                const templates = Array.isArray(templateData?.credentialTemplates)
                    ? templateData.credentialTemplates
                    : [];
                setTemplateOptions(
                    templates
                        .map((template: any) => ({
                            label: template?.name || template?.id,
                            value: template?.id
                        }))
                        .sort((a: any, b: any) =>
                            String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base", numeric: true })
                        )
                );
            } catch (templateError) {
                console.error("Error loading Inception credential templates:", templateError);
                setTemplateOptions([]);
            }
        } catch (error) {
            console.error("Error loading Inception settings:", error);
            setHasUserToken(false);
            setTemplateOptions([]);
            form.resetFields();
            notifyError(getErrorMessage(error, "Failed to load Inception settings"));
        } finally {
            setIsLoading(false);
        }
    }, [form, selectedBuildingId]);

    useEffect(() => {
        if (selectedTenantId) {
            loadSettings(selectedTenantId);
        }
    }, [loadSettings, selectedTenantId]);

    const onSave = async () => {
        if (!selectedTenantId) return;
        if (!selectedBuildingId) {
            notifyError("Missing company context");
            return;
        }

        try {
            const values = await form.validateFields();
            setIsSaving(true);
            const updated = await updateInceptionSettings(selectedBuildingId, selectedTenantId, {
                skytunnelUrl: values.skytunnelUrl,
                templateId: values.templateId,
                userToken: values.userToken
            });
            setHasUserToken(!!updated?.hasUserToken || !!values.userToken);
            form.setFieldsValue({
                skytunnelUrl: updated?.skytunnelUrl || values.skytunnelUrl,
                templateId: updated?.templateId || values.templateId,
                userToken: ""
            });
            notifySuccess("Inception settings updated");
        } catch (error) {
            console.error("Error saving Inception settings:", error);
            notifyError(getErrorMessage(error, "Failed to update Inception settings"));
        } finally {
            setIsSaving(false);
        }
    };

    const onTestConnection = async () => {
        try {
            const values = await form.validateFields(["skytunnelUrl", "templateId", "userToken"]);
            const userToken = values.userToken?.trim();
            if (!userToken) {
                notifyError("Please enter the API token to test the connection");
                return;
            }
            if (!selectedBuildingId) {
                notifyError("Missing company context");
                return;
            }

            setIsTesting(true);
            await testInceptionConnection({
                buildingId: selectedBuildingId,
                skytunnelUrl: values.skytunnelUrl,
                userToken,
                templateId: values.templateId
            });
            notifySuccess("Inception connection successful");
        } catch (error: any) {
            if (error?.errorFields) {
                notifyError("Please complete the required fields to test the connection");
                return;
            }
            notifyError(getErrorMessage(error, "Inception connection test failed"));
        } finally {
            setIsTesting(false);
        }
    };

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <Space direction="vertical" style={{ width: "100%" }}>
                <Row style={{ marginTop: 15, display: "flex", flexDirection: "column" }}>
                    <Title level={4} style={{ marginBottom: 4 }}>IR Inception Settings</Title>
                    <Text type="secondary">
                        URL is stored in IntegrationConfig. API token is stored in SSM and is never returned to frontend.
                    </Text>
                </Row>
                <Row className="grid-options">
                    <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
                        <Select
                            value={selectedCompanyKey || undefined}
                            onChange={setSelectedCompanyKey}
                            style={{ width: 260 }}
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select Company"
                            loading={isLoadingCompanies}
                        >
                            {companies.map((tenant: any) => (
                                <Option key={buildCompanyKey(tenant)} value={buildCompanyKey(tenant)}>{tenant.name}</Option>
                            ))}
                        </Select>
                    </Space>
                </Row>
            </Space>
            <Content className="table-wrapper">
                {selectedTenantId ? (
                    <Card title="Connection Details" bordered={false} style={{ maxWidth: 640 }}>
                        <Form form={form} layout="vertical" disabled={isLoading}>
                            <Form.Item
                                name="skytunnelUrl"
                                label="Skytunnel URL"
                                rules={[{ required: true, message: "Please enter the Skytunnel URL" }]}
                                extra="Example: https://your-skytunnel-url"
                            >
                                <Input placeholder="https://your-skytunnel-url" />
                            </Form.Item>
                            <Form.Item
                                name="userToken"
                                label="API Token"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            const token = typeof value === "string" ? value.trim() : "";
                                            if (hasUserToken || token) return Promise.resolve();
                                            return Promise.reject(new Error("Please enter the API token"));
                                        }
                                    }
                                ]}
                                extra={hasUserToken ? "Token already stored. Enter a new token only to rotate it." : undefined}
                            >
                                <Input.Password placeholder="API token" autoComplete="off" />
                            </Form.Item>
                            <Form.Item
                                name="templateId"
                                label="Inception Credential Template"
                                rules={[{ required: true, message: "Please select the Inception credential template" }]}
                            >
                                {templateOptions.length > 0 ? (
                                    <Select
                                        placeholder="Select Inception credential template"
                                        options={templateOptions}
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                ) : (
                                    <Input placeholder="Template ID" />
                                )}
                            </Form.Item>
                            <Form.Item style={{ marginTop: 8 }}>
                                <Button
                                    type="primary"
                                    className="btn btn--primary"
                                    onClick={onSave}
                                    loading={isSaving}
                                    disabled={isLoading || isTesting}
                                >
                                    Save Settings
                                </Button>
                                <Button
                                    className="btn btn--o-primary"
                                    onClick={onTestConnection}
                                    loading={isTesting}
                                    disabled={isLoading || isSaving}
                                >
                                    Test Connection
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                ) : (
                    <Alert
                        type="info"
                        showIcon
                        message="Select a company to configure IR Inception settings."
                    />
                )}
            </Content>
        </Space>
    );
};
